/**
 * Component for BackstageAlbum
 *
 * @module component/BackstageAlbum
 */

import { List } from 'immutable';
import noop from 'lodash/noop';
import PropTypes from 'prop-types';
import React from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Link } from 'react-router';

import { getCatalogDetailsForBackstage } from '../actions';
import { BackstageLoggerDefaultArgs, messages } from '../constants';
import * as D from '../deps';
import { getBackstageAnnotation, getCommonBackstageProps, isValidPandoraId } from '../utils';

import BackstageTrackList from './BackstageTrackList';
import BackstageAlbumGraphQL from './pages/BackstageAlbum/BackstageAlbum';

const { AUDIO_PLAY_TRACK, PAUSE } = D.Constants.AudioPlayer.AudioPlayerActions;
const { ADDTOCOLLECTION, REMOVEFROMCOLLECTION, SHARE } = D.Constants.SourceCard.SourceCardConstants;
const { STATION_TRIGGER } = D.Constants.SourceCard.SourceCardTriggerTypes;
const BackstageTrackListWithLogger = D.withVisibilityLogger(
  {
    contentSection: D.Constants.ContentSections.BackstageContentSections.TRACK_LIST,
  },
  { staticHeight: D.RowItemHeights.SMALL, useMemo: true },
)(BackstageTrackList);

export class BackstageAlbum extends React.Component {
  static propTypes = {
    children: PropTypes.element,
    getAlbum: PropTypes.func.isRequired,
    pandoraId: PropTypes.string,
    title: PropTypes.string,
    artistPandoraId: PropTypes.string,
    artistName: PropTypes.string,
    artistUrl: PropTypes.string,
    artistHasNoRadio: PropTypes.bool,
    albumImgUrl: PropTypes.string,
    description: PropTypes.string,
    albumTracks: ImmutablePropTypes.listOf(PropTypes.object),
    albumTrackIds: ImmutablePropTypes.listOf(PropTypes.string),
    openDropdown: PropTypes.func.isRequired,
    closeDropdown: PropTypes.func.isRequired,
    openShareModal: PropTypes.func.isRequired,
    closeModal: PropTypes.func,
    startArtistStation: PropTypes.func,
    descriptionTitle: PropTypes.object,
    playbackMessages: PropTypes.object,
    shouldRenderBSPSidebarButtons: PropTypes.bool,
    disableHover: PropTypes.bool,
    // T3 specific
    onDemandIsEnabled: PropTypes.bool,
    addCollectionItem: PropTypes.func,
    removeCollectionItem: PropTypes.func,
    sourceCardMenuItems: PropTypes.array,
    playResumeSource: PropTypes.func,
    pauseTrack: PropTypes.func,
    isPaused: PropTypes.bool,
    isPlayingCurrently: PropTypes.bool,
    openModal: PropTypes.func,
    releaseYear: PropTypes.number,
    totalTracks: PropTypes.number,
    getCatalogDetailsForBackstage: PropTypes.func,
    albumCopyrights: PropTypes.string,
    shareableUrlPath: PropTypes.string,
    isRightRailActive: PropTypes.bool,
    encryptedPandoraId: PropTypes.string,
    hasInteractiveRights: PropTypes.bool,
    inCollection: PropTypes.bool,
    showPremiumAccess: PropTypes.bool,
    isCompilation: PropTypes.bool,
    isMobileWeb: PropTypes.bool,
  };

  componentDidMount() {
    if (this.props.pandoraId) {
      this.props.getCatalogDetailsForBackstage(this.props.pandoraId, true);
    }
  }

  shouldComponentUpdate(nextProps) {
    return D.arePropsDifferent(nextProps, this.props);
  }

  componentDidUpdate(prevProps) {
    // Prevent a redundant call using the unencrypted pandoraId, if we already made data calls using the encryptedPandoraId
    const isPandoraIdUpdated = prevProps.pandoraId !== this.props.encryptedPandoraId;
    if (
      this.props.pandoraId &&
      prevProps.pandoraId !== this.props.pandoraId &&
      isPandoraIdUpdated
    ) {
      this.props.getCatalogDetailsForBackstage(this.props.pandoraId, true);
      this.props.getAlbum(D.getMusicIdFromPandoraId(this.props.pandoraId), false, false);
    }
  }

  static contextType = D.AppContext;

  getStartArtistStationMessage() {
    if (this.props.artistHasNoRadio) return null;
    return (
      <FormattedMessage
        {...messages.startArtistStationBasedOnArtist}
        values={{ artistName: this.props.artistName }}
      />
    );
  }

  getStartStationButton() {
    if (this.props.artistHasNoRadio) return null;
    return [
      <D.ButtonT3 key={0} size={D.ButtonT3Size.SMALL} onClick={this.onModalStartStationClick}>
        <FormattedMessage {...this.props.playbackMessages.startArtistStation} />
      </D.ButtonT3>,
    ];
  }

  getIntegrationDataFactory = name => {
    return () => {
      const contexts = {
        buttonRow: {
          ...BackstageLoggerDefaultArgs.controlBar,
          ...{ pandoraId: this.props.pandoraId },
        },
      };
      return contexts[name] || BackstageLoggerDefaultArgs[name];
    };
  };

  getShareParentSource = () => {
    return {
      shareParentSourceId: this.props.pandoraId,
      shareParentSourceType: D.PandoraTypes.ALBUM,
    };
  };

  onModalStartStationClick = () => {
    this.props.closeModal();
    this.props.startArtistStation({
      pandoraId: this.props.artistPandoraId,
      rerouteBasedOnTier: true,
      componentName: 'BackstageAlbum',
    });
  };

  triggerStartArtistStationCoachmark = () => {
    this.context.integration.action.clicked({
      ...BackstageLoggerDefaultArgs.controlBar,
      ...{
        pandoraId: this.props.pandoraId,
        componentEventType: STATION_TRIGGER,
      },
    });
    this.props.openModal({
      modalType: 'ModalCoachMark',
      body: (
        <span>
          <FormattedMessage {...messages.albumStationsNotAvailable} />{' '}
          {this.getStartArtistStationMessage()}
        </span>
      ),
      buttons: this.getStartStationButton(),
    });
  };

  playOnClick = () => {
    const { pandoraId, showPremiumAccess } = this.props;
    this.context.integration.action.clicked({
      ...BackstageLoggerDefaultArgs.controlBar,
      ...{ pandoraId, componentEventType: AUDIO_PLAY_TRACK },
    });
    this.props.playResumeSource({
      id: pandoraId,
      pandoraType: null,
      forceRestartSourceIfPlaying: showPremiumAccess,
      context: D.Constants.Ads.Rewards.ContextTypes.BACKSTAGE,
      shouldShuffle: false,
      pageUrl: this.props.shareableUrlPath,
    });
  };

  pauseOnClick = () => {
    this.context.integration.action.clicked({
      ...BackstageLoggerDefaultArgs.controlBar,
      ...{ componentEventType: PAUSE },
    });
    this.props.pauseTrack();
  };

  onShareClick = () => {
    this.context.integration.action.clicked({
      ...BackstageLoggerDefaultArgs.controlBar,
      ...{ componentEventType: SHARE },
    });
    this.props.openShareModal(null, this.getShareParentSource());
  };

  addToMusicClick = () => {
    const { pandoraId } = this.props;
    this.context.integration.action.clicked({
      ...BackstageLoggerDefaultArgs.controlBar,
      ...{ pandoraId, componentEventType: ADDTOCOLLECTION },
    });
    this.props.addCollectionItem({ pandoraId });
  };

  removeMusicClick = () => {
    this.context.integration.action.clicked({
      ...BackstageLoggerDefaultArgs.controlBar,
      ...{ componentEventType: REMOVEFROMCOLLECTION },
    });
    this.props.removeCollectionItem({ pandoraId: this.props.pandoraId });
  };

  onHeaderLinkClick = e => {
    this.context.integration.action.clicked({
      ...BackstageLoggerDefaultArgs.header,
      ...{ destinationUrl: e.currentTarget.pathname },
    });
  };

  renderSideBarButtons = () => {
    const { inCollection, isPaused } = this.props;
    const playOnClick = !this.props.isPlayingCurrently || isPaused ? this.playOnClick : noop;
    const pauseOnClick = this.props.isPlayingCurrently && !isPaused ? this.pauseOnClick : noop;
    const addToMusicOnClick = !inCollection ? this.addToMusicClick : noop;
    const removeFromMusicOnClick = inCollection ? this.removeMusicClick : noop;
    const disabledButtons = [D.ButtonRowType.CREATE_STATION];

    if (this.props.hasInteractiveRights === false) {
      disabledButtons.push(D.ButtonRowType.PLAY);
    }

    const buttonRowProps = {
      playOnClick,
      pauseOnClick,
      removeFromMusicOnClick,
      disabledButtons,
      createStationOnClick: this.triggerStartArtistStationCoachmark,
      shareOnClick: this.onShareClick,
      isSidebar: true,
      buttonTextType: D.ButtonTextType.PLAY_ALBUM,
      ellipsisSourceCardMenuItems: this.props.sourceCardMenuItems,
    };

    if (this.props.onDemandIsEnabled) {
      buttonRowProps.addToMusicOnClick = addToMusicOnClick;
    } else {
      buttonRowProps.hiddenButtons = [D.ButtonRowType.MORE];
    }

    return (
      <D.AppContextIntegrationData data={this.getIntegrationDataFactory('buttonRow')}>
        <D.ButtonRowWithLogger {...buttonRowProps} />
      </D.AppContextIntegrationData>
    );
  };

  renderAlbumDescription() {
    if (!this.props.description) {
      return;
    }

    return (
      <D.ContentSection isAlbumDescription={true}>
        <D.DescriptionBackstageWithLogger
          text={this.props.description}
          linkUrl={D.getBackstageFullDescriptionPathForShareUrl(this.props.shareableUrlPath)}
          isExpanded={false}
          linkText={messages.readMore}
          disableHover={this.props.disableHover}
        />
      </D.ContentSection>
    );
  }

  renderTracks() {
    const trackIds = this.props.albumTrackIds;
    if (!trackIds) {
      return;
    }
    return (
      <D.AppContextIntegrationData data={this.getIntegrationDataFactory('trackList')}>
        <BackstageTrackListWithLogger
          isCompilation={this.props.isCompilation}
          tracks={trackIds}
          isAlbumBSP={true}
          playablePandoraId={this.props.pandoraId}
          elementHeight={D.RowItemHeights.SMALL}
          isAlbumRadioOnly={!this.props.hasInteractiveRights}
          isAlbumCollected={this.props.inCollection}
          shareParentSource={this.getShareParentSource()}
        />
      </D.AppContextIntegrationData>
    );
  }

  renderStructuredMetaData = () => {
    const { artistPandoraId } = this.props;
    if (!artistPandoraId) {
      return;
    }
    return (
      <D.StructuredMetadata
        isAlbum={true}
        shareableUrlPath={this.props.shareableUrlPath}
        artistShareableUrlPath={this.props.artistUrl}
        title={this.props.title}
        artistName={this.props.artistName}
        pandoraId={this.props.artistPandoraId}
        description={this.props.description}
        image={this.props.albumImgUrl}
        albumTracks={this.props.albumTracks}
      />
    );
  };

  renderMainContent = () => {
    if (this.props.children) {
      return React.cloneElement(this.props.children, this.props);
    }
    return (
      <React.Fragment>
        {this.renderAlbumDescription()}
        {this.renderTracks()}
        {this.props.albumCopyrights && (
          <D.CopyrightWithLogger copyrights={this.props.albumCopyrights} />
        )}
      </React.Fragment>
    );
  };

  render() {
    const { title, artistName, totalTracks, releaseYear, artistUrl, shareableUrlPath } = this.props;

    let subTitleLrg;
    let subTitleSm;

    if (artistName) {
      if (artistUrl) {
        subTitleLrg = (
          <span>
            <FormattedMessage {...messages.albumBy} />
            <Link
              className={D.HeaderBackstageStyles.SUBTITLE_LINK}
              to={artistUrl}
              data-qa={D.QA.HSTSTL}
              onClick={this.onHeaderLinkClick}
            >
              {artistName}
            </Link>
          </span>
        );
      } else {
        subTitleLrg = (
          <span>
            <FormattedMessage {...messages.albumBy} />
            {artistName}
          </span>
        );
      }
    }
    if (totalTracks && releaseYear) {
      subTitleSm = (
        <span data-qa={D.QA.HSTST}>
          <FormattedMessage {...messages.totalSongs} values={{ trackCount: totalTracks }} />
          {` - ${releaseYear}`}
        </span>
      );
    } else if (totalTracks) {
      subTitleSm = (
        <span data-qa={D.QA.HSTST}>
          <FormattedMessage {...messages.totalSongs} values={{ trackCount: totalTracks }} />
        </span>
      );
    } else if (releaseYear) {
      subTitleSm = <span data-qa={D.QA.HSTST}>{releaseYear}</span>;
    }

    const headerProps = {
      title: title || '',
      titleUrl: shareableUrlPath,
      disableHover: this.props.disableHover,
      badges: this.props.hasInteractiveRights === false ? List([D.BadgeTypes.RADIO]) : null,
      subTitleLrg,
      subTitleSm,
    };

    return (
      <D.BackstageLayout
        headerProps={headerProps}
        heroImageUrl={this.props.albumImgUrl}
        heroImageLinkUrl={this.props.shareableUrlPath}
        renderSidebarButtons={
          this.props.shouldRenderBSPSidebarButtons ? this.renderSideBarButtons : null
        }
        renderArtworkPlayButton={this.props.isMobileWeb}
        renderMainContent={this.renderMainContent}
        entityIdentifier={this.props.pandoraId}
        entityGetterFunc={this.props.getCatalogDetailsForBackstage}
        playOnClick={this.playOnClick}
        {...this.props}
      />
    );
  }
}

export function mapStateToProps(state, ownProps) {
  const encryptedPandoraId = ownProps && ownProps.params && ownProps.params.pandoraId;
  const annotation = getBackstageAnnotation(state, encryptedPandoraId);

  if (
    !annotation ||
    annotation.isEmpty() ||
    ![D.PandoraTypes.ALBUM].includes(annotation.get('type'))
  ) {
    return {
      pandoraId: encryptedPandoraId ? encryptedPandoraId : null,
    };
  }
  const pandoraId = annotation.get('pandoraId');
  const catalogDetails = D.getInDomain('catalog.utils.getCatalogDetailsFromState')(
    state,
    pandoraId,
  );
  const props = getCommonBackstageProps(state, encryptedPandoraId, pandoraId);

  if (catalogDetails && !catalogDetails.isEmpty() && annotation) {
    const getAnnotations = D.getInDomain('catalog.utils.getAnnotations');
    props.albumTrackIds = D.getFromMap(annotation, 'tracks') || List();
    props.albumTracks = props.albumTrackIds.map(trackPandoraId => {
      return getAnnotations(state, trackPandoraId);
    });
    props.sourceCardMenuItems = D.getSourceCardMenuItems({
      state,
      pandoraId,
      pandoraType: D.PandoraTypes.ALBUM,
      isBackstagePage: true,
    });

    const rightsInfo = D.getFromMap(annotation, 'rightsInfo');
    props.hasInteractiveRights = D.getFromMap(rightsInfo, 'hasInteractive');

    props.isRightRailActive =
      D.getInDomain('ads.utils.getIsRightRailActive', false)(state) ||
      D.getInDomain('ads.utils.getIsElementCenteredWithAds', false)(state);
    props.isPaused = !D.getInDomain('playback.utils.isAudioPlaying', false)(state);
    props.isPlayingCurrently = D.getInDomain('playback.utils.isSourcePlayingCurrently', false)(
      state,
      pandoraId,
    );
    props.inCollection = D.getInDomain('collection.utils.isPandoraIdInCollection', false)(
      state,
      pandoraId,
    );
    props.playbackMessages = D.getInDomain('playback.constants.PlaybackMessages', null);

    props.albumImgUrl = D.getArt(annotation, 1080, state);
    props.title = D.getFromMap(annotation, 'name');
    props.albumCopyrights = D.getFromMap(catalogDetails, 'copyright');
    props.shareableUrlPath = D.getFromMap(annotation, 'shareableUrlPath');
    props.totalTracks = D.getFromMap(annotation, 'trackCount');
    props.isCompilation = D.getFromMap(annotation, 'isCompilation');

    if (D.getFromMap(annotation, 'releaseDate')) {
      props.releaseYear = new Date(D.getFromMap(annotation, 'releaseDate')).getFullYear();
    }

    const artistPandoraId = D.getFromMap(annotation, 'artistId');
    const artistAnnotation = D.getAnnotationsById(state, artistPandoraId);
    const artistLink = D.getInDomain('catalog.utils.getCatalogEntityLink')(state, artistPandoraId);
    props.artistPandoraId = artistPandoraId;
    props.artistName = artistLink.name;
    props.artistUrl = artistLink.shareableUrlPath;
    props.artistHasNoRadio = D.Utils.Rights.hasNoRadio(artistAnnotation);
    props.descriptionTitle = <FormattedMessage {...messages.albumDescription} />;
    props.description = D.getFromMap(catalogDetails, 'description');
  }

  props.showPremiumAccess = D.Utils.Listener.showPremiumAccess(state);
  props.shouldRenderBSPSidebarButtons = D.shouldRenderBSPSidebarButtons(state);
  props.isMobileWeb = D.isMobileWeb(state);
  props.disableHover = D.disableHoverStates(state);
  props.isInitialAppLoad = D.getInDomain('application.utils.isInitialLoad', false)(state);

  return props;
}

const mapDispatchToProps = {
  getAlbum: D.getAlbum,
  openDropdown: D.openDropdown,
  closeDropdown: D.closeDropdown,
  addCollectionItem: D.addCollectionItem,
  removeCollectionItem: D.removeCollectionItem,
  playResumeSource: D.playResumeSource,
  pauseTrack: D.Actions.UseCases.pauseTrack,
  openShareModal: D.openShareModal,
  closeModal: D.closeModal,
  startArtistStation: D.Actions.UseCases.startStation,
  openModal: D.openModal,
  getCatalogDetailsForBackstage,
};

const BackstageAlbumBase = props => {
  const { pandoraId, children, isInitialAppLoad } = props;

  if (isInitialAppLoad || !isValidPandoraId(pandoraId)) {
    return <BackstageAlbum {...props} />;
  }

  return <BackstageAlbumGraphQL pandoraId={pandoraId}>{children}</BackstageAlbumGraphQL>;
};

BackstageAlbumBase.propTypes = {
  pandoraId: PropTypes.string,
  children: PropTypes.element,
  isInitialAppLoad: PropTypes.bool,
};

export default connect(mapStateToProps, mapDispatchToProps)(BackstageAlbumBase);
